import {
  databases,
  DATABASE_ID,
  COLLECTION_ID_VIR_REPORTS
} from '../config/appwrite';
import { ID, Query } from 'appwrite';

const MAX_ATTACHMENT_CHARS = 900000;

const safeJsonParse = (raw, fallback) => {
  if (raw == null || raw === '') return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** Map Appwrite document → VIR row used by Fleet Maintenance. */
export const formatVirReport = (doc) => ({
  id: doc.$id ?? doc.id,
  vehicleId: doc.vehicleId || '',
  plate: doc.plate || '',
  driver: doc.driver || '',
  inspector: doc.inspector || '',
  virNo: doc.virNo || '',
  date: doc.inspectionDate || '',
  inspectionDate: doc.inspectionDate || '',
  inspectedDate: doc.inspectedDate || null,
  overall: doc.overall || 'inspect',
  odometer: toNumberOrNull(doc.odometer),
  notes: doc.notes || '',
  project: doc.project || '',
  checks: safeJsonParse(doc.checksJson, {}),
  tickets: safeJsonParse(doc.ticketsJson, {}),
  attachmentPhoto: doc.attachmentPhoto || null,
  attachmentPhotoName: doc.attachmentPhotoName || '',
  submittedByEmail: doc.submittedByEmail || '',
  submittedByName: doc.submittedByName || '',
  scheduled: doc.overall === 'inspect'
});

const toPayload = (fields) => {
  const attachment =
    fields.attachmentPhoto && String(fields.attachmentPhoto).length <= MAX_ATTACHMENT_CHARS
      ? String(fields.attachmentPhoto)
      : '';

  return {
    vehicleId: String(fields.vehicleId || '').trim(),
    plate: String(fields.plate || '').trim().toUpperCase().replace(/\s/g, ''),
    driver: String(fields.driver || '').trim(),
    inspector: String(fields.inspector || fields.driver || '').trim(),
    virNo: String(fields.virNo || '').trim(),
    inspectionDate: String(fields.inspectionDate || fields.date || '').trim(),
    inspectedDate: String(fields.inspectedDate || '').trim(),
    overall: ['pass', 'fail', 'inspect'].includes(fields.overall) ? fields.overall : 'pass',
    odometer: toNumberOrNull(fields.odometer),
    notes: String(fields.notes || '').trim(),
    project: String(fields.project || '').trim(),
    checksJson: JSON.stringify(fields.checks || {}),
    ticketsJson: JSON.stringify(fields.tickets || {}),
    attachmentPhoto: attachment,
    attachmentPhotoName: String(fields.attachmentPhotoName || '').trim(),
    submittedByEmail: String(fields.submittedByEmail || '').trim().toLowerCase(),
    submittedByName: String(fields.submittedByName || '').trim()
  };
};

export const getVirReportErrorMessage = (error) => {
  if (!error) return 'Unknown error';
  const msg = error.message || error.response?.message;
  if (typeof msg === 'string' && msg.trim()) return msg.trim();
  if (error.code) return `Error ${error.code}`;
  return 'Request failed';
};

/** Fetch all VIR reports for a vehicle id and/or plate (paginated). */
export const fetchVirReportsForVehicle = async ({ vehicleId, plate } = {}) => {
  const all = [];
  // eslint-disable-next-line no-unused-vars
  let cursor = null;
  const limit = 100;
  const plateNorm = String(plate || '')
    .trim()
    .toUpperCase()
    .replace(/\s/g, '');
  const id = String(vehicleId || '').trim();

  const runList = async (extraQueries) => {
    const pageDocs = [];
    let pageCursor = null;
    for (let page = 0; page < 20; page += 1) {
      const queries = [Query.limit(limit), Query.orderDesc('inspectionDate'), ...extraQueries];
      if (pageCursor) queries.push(Query.cursorAfter(pageCursor));
      const response = await databases.listDocuments(
        DATABASE_ID,
        COLLECTION_ID_VIR_REPORTS,
        queries
      );
      const docs = response.documents || [];
      pageDocs.push(...docs);
      if (docs.length < limit) break;
      pageCursor = docs[docs.length - 1].$id;
    }
    return pageDocs;
  };

  if (id) {
    const docs = await runList([Query.equal('vehicleId', id)]);
    all.push(...docs.map(formatVirReport));
  }

  if (plateNorm) {
    const docs = await runList([Query.equal('plate', plateNorm)]);
    const seen = new Set(all.map((r) => r.id));
    docs.forEach((doc) => {
      const row = formatVirReport(doc);
      if (!seen.has(row.id)) all.push(row);
    });
  }

  return all.sort((a, b) => String(b.date).localeCompare(String(a.date)));
};

/** Fetch all VIR reports (paginated) for fleet-wide alerts / notifications. */
export const fetchAllVirReports = async () => {
  const all = [];
  let cursor = null;
  const limit = 100;
  for (let page = 0; page < 50; page += 1) {
    const queries = [Query.limit(limit), Query.orderDesc('inspectionDate')];
    if (cursor) queries.push(Query.cursorAfter(cursor));
    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTION_ID_VIR_REPORTS,
      queries
    );
    const docs = response.documents || [];
    all.push(...docs.map(formatVirReport));
    if (docs.length < limit) break;
    cursor = docs[docs.length - 1].$id;
  }
  return all;
};

/** Find existing report for vehicle + scheduled inspection date. */
export const findVirReportByVehicleDate = async (vehicleId, inspectionDate, plate = '') => {
  const date = String(inspectionDate || '').trim();
  if (!date) return null;

  const tryQuery = async (queries) => {
    const response = await databases.listDocuments(DATABASE_ID, COLLECTION_ID_VIR_REPORTS, [
      ...queries,
      Query.equal('inspectionDate', date),
      Query.limit(1)
    ]);
    return response.documents?.[0] ? formatVirReport(response.documents[0]) : null;
  };

  if (vehicleId) {
    const byId = await tryQuery([Query.equal('vehicleId', String(vehicleId))]);
    if (byId) return byId;
  }

  const plateNorm = String(plate || '')
    .trim()
    .toUpperCase()
    .replace(/\s/g, '');
  if (plateNorm) {
    return tryQuery([Query.equal('plate', plateNorm)]);
  }
  return null;
};

/** Create or update a VIR report for a vehicle + inspection date. */
export const upsertVirReport = async (fields) => {
  const payload = toPayload(fields);
  if (!payload.vehicleId || !payload.inspectionDate) {
    throw new Error('vehicleId and inspectionDate are required.');
  }

  const existing = await findVirReportByVehicleDate(
    payload.vehicleId,
    payload.inspectionDate,
    payload.plate
  );

  if (existing?.id) {
    const updated = await databases.updateDocument(
      DATABASE_ID,
      COLLECTION_ID_VIR_REPORTS,
      existing.id,
      payload
    );
    return formatVirReport(updated);
  }

  const created = await databases.createDocument(
    DATABASE_ID,
    COLLECTION_ID_VIR_REPORTS,
    ID.unique(),
    payload
  );
  return formatVirReport(created);
};
